import { EmptyState } from '@/components/primitives/EmptyState'
import { ErrorState } from '@/components/primitives/ErrorState'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  type ColumnDef,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
  type Table as TanstackTable,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { motion } from 'framer-motion'
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'

export type DataTableColumn<T> = ColumnDef<T, unknown>

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  data: T[] | undefined
  isLoading?: boolean
  isFetching?: boolean
  error?: unknown
  onRetry?: () => void
  getRowId?: (row: T) => string
  onRowClick?: (row: T) => void
  selection?: RowSelectionState
  onSelectionChange?: (selection: RowSelectionState) => void
  enableSelection?: boolean
  search?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  toolbarLeading?: ReactNode
  toolbarTrailing?: ReactNode
  emptyTitle?: string
  emptyDescription?: string
  emptyAction?: ReactNode
  pageSize?: number
  className?: string
  /** Render below the table — bulk action bar typically lives at page-level. */
  children?: (table: TanstackTable<T>) => ReactNode
}

export function DataTable<T>({
  columns,
  data,
  isLoading = false,
  isFetching = false,
  error,
  onRetry,
  getRowId,
  onRowClick,
  selection,
  onSelectionChange,
  enableSelection = false,
  search,
  onSearchChange,
  searchPlaceholder = 'Search…',
  toolbarLeading,
  toolbarTrailing,
  emptyTitle = 'Nothing here yet',
  emptyDescription = "When records show up, you'll see them here.",
  emptyAction,
  pageSize = 25,
  className,
  children
}: DataTableProps<T>) {
  const [internalSelection, setInternalSelection] = useState<RowSelectionState>(
    {}
  )
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const resolvedColumns = useMemo<DataTableColumn<T>[]>(() => {
    if (!enableSelection) return columns
    const selectionColumn: DataTableColumn<T> = {
      id: '__select',
      enableSorting: false,
      size: 32,
      header: ({ table }) => (
        <Checkbox
          aria-label="Select all rows"
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          aria-label="Select row"
          checked={row.getIsSelected()}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          onClick={(e) => e.stopPropagation()}
        />
      )
    }
    return [selectionColumn, ...columns]
  }, [columns, enableSelection])

  const rowSelection = selection ?? internalSelection
  const handleSelectionChange = (updater: unknown) => {
    const next =
      typeof updater === 'function'
        ? (updater as (s: RowSelectionState) => RowSelectionState)(rowSelection)
        : (updater as RowSelectionState)
    if (onSelectionChange) onSelectionChange(next)
    else setInternalSelection(next)
  }

  const table = useReactTable<T>({
    data: data ?? [],
    columns: resolvedColumns,
    state: {
      rowSelection,
      sorting,
      columnFilters
    },
    enableRowSelection: enableSelection,
    onRowSelectionChange: handleSelectionChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize }
    }
  })

  const showSearch = onSearchChange !== undefined
  const totalRows = data?.length ?? 0
  const showPagination = totalRows > pageSize

  return (
    <div className={cn('space-y-3', className)}>
      {(showSearch || toolbarLeading || toolbarTrailing) && (
        <div className="flex flex-wrap items-center gap-2">
          {showSearch && (
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search ?? ''}
                onChange={(e) => onSearchChange!(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-8"
              />
            </div>
          )}
          {toolbarLeading}
          <div className="ml-auto flex items-center gap-2">
            {isFetching && !isLoading && (
              <span className="text-xs text-muted-foreground">Refreshing…</span>
            )}
            {toolbarTrailing}
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b">
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const sortDir = header.column.getIsSorted()
                  return (
                    <th
                      key={header.id}
                      className="select-none px-3 py-2.5 text-left font-medium"
                      style={{ width: header.getSize() ?? undefined }}
                    >
                      {header.isPlaceholder ? null : !canSort ? (
                        // No sort button around it: a button can't hold the select-all checkbox.
                        flexRender(header.column.columnDef.header, header.getContext())
                      ) : (
                        <button
                          type="button"
                          disabled={!canSort}
                          onClick={header.column.getToggleSortingHandler()}
                          className={cn(
                            'inline-flex items-center gap-1.5',
                            canSort && 'cursor-pointer hover:text-foreground'
                          )}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {canSort &&
                            (sortDir === 'asc' ? (
                              <ArrowUp className="size-3" />
                            ) : sortDir === 'desc' ? (
                              <ArrowDown className="size-3" />
                            ) : (
                              <ArrowUpDown className="size-3 opacity-40" />
                            ))}
                        </button>
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b last:border-0">
                  {resolvedColumns.map((_col, j) => (
                    <td key={j} className="px-3 py-2.5">
                      <Skeleton className="h-4 w-full max-w-[180px]" />
                    </td>
                  ))}
                </tr>
              ))
            ) : error ? (
              <tr>
                <td colSpan={resolvedColumns.length} className="p-0">
                  <ErrorState error={error} onRetry={onRetry} className="m-3" />
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={resolvedColumns.length} className="p-0">
                  <EmptyState
                    title={emptyTitle}
                    description={emptyDescription}
                    action={emptyAction}
                    className="m-3"
                  />
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row, idx) => (
                <motion.tr
                  key={row.id}
                  initial={idx < 12 ? { opacity: 0, y: 4 } : false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.18,
                    delay: idx < 12 ? idx * 0.02 : 0
                  }}
                  onClick={
                    onRowClick ? () => onRowClick(row.original) : undefined
                  }
                  className={cn(
                    'border-b transition-colors last:border-0',
                    onRowClick && 'cursor-pointer hover:bg-muted/40',
                    row.getIsSelected() && 'bg-primary/5'
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2.5 align-middle">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing{' '}
            <span className="font-medium text-foreground tabular-nums">
              {table.getRowModel().rows.length}
            </span>{' '}
            of{' '}
            <span className="font-medium text-foreground tabular-nums">
              {totalRows}
            </span>
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {children?.(table)}
    </div>
  )
}
